import {useEffect} from 'react'
import {useLocation} from 'react-router-dom'

/* WHY: pages render asynchronously (TanStack Query, lazy imports). On the
   pathname-change tick the new page's <h1> often is not in the DOM yet.
   Retry up to RETRY_FRAMES animation frames before giving up so the focus
   anchor lands on the new page heading.

   The announcement is owned by PageH1 (App.tsx) — it watches the title
   context and fires announce() when it changes. Doing the announce here
   too would either fire the STALE title (the central H1 hasn't re-rendered
   with the new title yet when this effect runs) or fire duplicates. Focus
   only. Issue 800. */
const RETRY_FRAMES = 30

export function useFocusOnRouteChange() {
    const location = useLocation()

    useEffect(() => {
        let cancelled = false
        let framesLeft = RETRY_FRAMES

        const focusElement = (el: HTMLElement) => {
            /* eslint-disable i18next/no-literal-string -- DOM attribute names, not user-facing strings */
            if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1')
            /* eslint-enable i18next/no-literal-string */
            el.focus({preventScroll: false, focusVisible: false} as FocusOptions)
        }

        const fallback = () => {
            const main = document.querySelector('main') as HTMLElement | null
            if (main) {
                focusElement(main)
                return
            }
            console.warn('useFocusOnRouteChange: no <main> element found on this page')
            document.body.focus()
        }

        const accessibleText = (el: HTMLElement): string => {
            const clone = el.cloneNode(true) as HTMLElement
            // eslint-disable-next-line i18next/no-literal-string -- CSS attribute selector
            clone.querySelectorAll('[aria-hidden="true"]').forEach((n) => n.remove())
            return (clone.textContent ?? '').trim()
        }

        const tryH1 = (): boolean => {
            if (cancelled) return true
            const h1 = document.querySelector('h1') as HTMLElement | null
            if (h1 && accessibleText(h1)) {
                focusElement(h1)
                return true
            }
            return false
        }

        const retry = () => {
            if (cancelled) return
            if (tryH1()) return
            if (framesLeft-- > 0) {
                requestAnimationFrame(retry)
                return
            }
            fallback()
        }

        /* Try synchronously first — when the H1 is already in the DOM (which
           is the common case in unit tests using renderHook against a static
           document) focus lands immediately. Only if the H1 hasn't rendered
           yet do we fall into the RAF-retry loop. */
        if (tryH1()) {
            return () => { cancelled = true }
        }
        if (!document.querySelector('main') && !document.querySelector('h1')) {
            // Nothing in the DOM at all — fall through immediately so tests
            // don't spin through 30 RAF cycles waiting on a page that will
            // never render.
            fallback()
            return () => { cancelled = true }
        }
        requestAnimationFrame(retry)
        return () => { cancelled = true }
    }, [location.pathname])
}
